// Provider-account degradation reported by the org gateway routes
// (GET /organization/:orgId/gateways and .../gateway/:id).
//
// When the provider details for one gateway cannot be fetched, the backend keeps the
// row in the list and replaces `gateway_data` with a single `account_status` entry
// instead of failing the whole request. So on such a row every other gateway_data
// field (bank name, account number, representative, ...) is absent, and the UI has to
// say why the provider section is empty rather than render blanks.
//
// Mirrors v2/model/charidy_org_merchant_info/errors.go in go.charidy.com.

export const ACCOUNT_STATUS_KEY = "account_status";

// The provider refuses our credentials for this account (revoked Stripe Connect
// link, deleted account, account that never lived on our platform). Permanent —
// retrying never helps, the org has to reconnect.
export const ACCOUNT_STATUS_INACCESSIBLE = "inaccessible";

// Fetching the provider details failed for another reason (transient provider
// fault). May recover on its own.
export const ACCOUNT_STATUS_UNAVAILABLE = "unavailable";

const ACCOUNT_STATUS_COPY = {
  [ACCOUNT_STATUS_INACCESSIBLE]: {
    variant: "danger",
    labelKey: "gateways.account_status_inaccessible",
    labelDefault: "Reconnect required",
    messageKey: "gateways.account_status_inaccessible_info",
    messageDefault:
      "We can no longer read this account at the payment provider, so its details are unavailable. Reconnect the account to keep accepting donations through this gateway.",
  },
  [ACCOUNT_STATUS_UNAVAILABLE]: {
    variant: "warning",
    labelKey: "gateways.account_status_unavailable",
    labelDefault: "Temporarily unavailable",
    messageKey: "gateways.account_status_unavailable_info",
    messageDefault:
      "The payment provider did not return this account's details. This is usually temporary — reload in a few minutes, and contact support if it persists.",
  },
};

/** Degradation copy for one gateway, or null when its provider data loaded fine.
 * @param {Object} attributes gateway record `attributes`
 * @returns {?{status: string, variant: string, labelKey: string, labelDefault: string, messageKey: string, messageDefault: string}}
 */
export function getAccountStatus(attributes) {
  const status = attributes?.gateway_data?.[ACCOUNT_STATUS_KEY];

  if (!status) {
    return null;
  }

  // an unknown future status still means "provider data missing" — degrade to the
  // recoverable copy rather than rendering a blank section.
  const copy = ACCOUNT_STATUS_COPY[status] || ACCOUNT_STATUS_COPY[ACCOUNT_STATUS_UNAVAILABLE];

  return { status, ...copy };
}
